<template>
  <FormKit id="contactInformationForm" name="contactInformationForm" type="form" @submit="onSave">
    <div class="flex flex-col gap-6 mt-4">
      <div class="items-center w-full">
        <RACQFormKit ref="emailEl" v-model="emailAddress" :bindings="emailBindings" @blur="(e: any) => checkEmail(e)" />
        <p class="text-xs" v-html="emailAddressInput?.postContent?.value"></p>
      </div>
      <div class="items-center md:max-w-[384px]">
        <PhoneNumberInput ref="primaryPhoneNumberEl" v-model="primaryPhoneNumber" :bindings="phoneNumberBindings" />
      </div>
      <div class="items-center md:max-w-[384px]">
        <PhoneNumberInput
          ref="additionalPhoneNumberEl"
          v-model="additionalPhoneNumber"
          :bindings="additionalPhoneNumberBindings"
        />
      </div>
      <div class="flex w-[640px] items-start content-start gap-4 flex-wrap mt-6">
        <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
          <div class="flex items-center space-x-2">
            <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
            <span>{{ discardChangesLink?.link?.value?.text }}</span>
          </div>
        </Link>
        <button type="submit" :disabled="!hasChanges || emailAlreadyInUse" class="btn btn-md btn-primary">
          Save changes
        </button>
      </div>
    </div>
    <FullScreenSpinner v-if="showSaving" :applyOpacity="true"></FullScreenSpinner>
  </FormKit>
</template>
<script setup lang="ts">
import { onMounted, ref, watch, nextTick } from 'vue';
import { FormKit } from '@formkit/vue';
import { useAccountStore } from '@/store';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import PhoneNumberInput from '@/components/PhoneNumberInput/PhoneNumberInput.vue'; //
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { cloneDeep } from 'lodash-es';
import { getFormInput } from '@/utils/form-input-helper';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();

const emit = defineEmits(['emailIsTaken', 'update:modelValue', 'blur']);

const emailEl = ref<any>();
const primaryPhoneNumberEl = ref<any>();
const additionalPhoneNumberEl = ref<any>();

const emailAddress = ref<string>('');
const primaryPhoneNumber = ref<string>('');
const additionalPhoneNumber = ref<string>('');
const showSaving = ref<boolean>(false);
const hasChanges = ref<boolean>(false);
const emailAlreadyInUse = ref<boolean>(false);
const watcherReady = ref(false);

const props = defineProps<{
  emailAddressInput: ISitecoreFormGenericInput;
  phoneNumberInput: ISitecoreFormGenericInput;
  additionalPhoneNumberInput: ISitecoreFormGenericInput;
  profileLink: any;
  content?: any;
}>();

const createBinding = (input: ISitecoreFormGenericInput, type: string) => {
  const bindings = {
    label: input?.label?.value,
    name: input?.name,
    type: type,
    placeholder: '',
    postContent: input?.postContent?.value,
    preContent: input?.preContent?.value,
    postContentClass: 'richtext',
    preContentClass: 'richtext',
    prefixIcon: '',
    suffixIcon: '',
    help: '',
    helpSm: '',
    helpMd: '',
    disablePaste: false,
    classesOptions: '',
    showClearIcon: false,
    minlength: null,
    maxlength: 100,
    autocomplete: 'off',
    innerClass: input?.innerClass?.value,
    outerClass: input?.outerClass?.value,
  };

  setupValidation(bindings, input);
  return bindings;
};

const setupValidation = (bindings: any, input: ISitecoreFormGenericInput) => {
  if (input?.isRequired?.value) {
    bindings.validation = [...(bindings.validation || []), ['required', 'trim']];

    if (input?.isRequiredErrorMessage?.value) {
      bindings.validationMessages = {
        ...(bindings.validationMessages || {}),
        required: input?.isRequiredErrorMessage?.value,
      };
    }
  }

  if (input?.validationRegex?.value) {
    bindings.validation = [...(bindings.validation || []), ['matches', new RegExp(input.validationRegex.value)]];

    bindings.validationRegex = input?.validationRegex?.value;

    if (input?.validationRegexErrorMessage?.value) {
      bindings.validationMessages = {
        ...(bindings.validationMessages || {}),
        matches: input?.validationRegexErrorMessage?.value,
      };
    }
  }
};

const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));
const emailBindings = createBinding(props.emailAddressInput, 'email');
const phoneNumberBindings = createBinding(props.phoneNumberInput, 'tel');
const additionalPhoneNumberBindings = createBinding(props.additionalPhoneNumberInput, 'tel');

onMounted(() => {
  setFieldsFromIdentityDetails();

  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }
});

watch(
  [
    emailAddress,
    primaryPhoneNumber,
    additionalPhoneNumber,
    primaryPhoneNumberEl.value?.node?.store?.rule_matches?.blocking,
    additionalPhoneNumberEl.value?.node?.store?.rule_matches?.blocking,
  ],
  () => {
    if (watcherReady.value) {
      hasChanges.value = true;
    }
  }
);

const checkEmail = async (e: any) => {
  //If have blocking regex don't bother validating
  if (emailEl.value?.inputRef?.node?.store?.rule_matches?.blocking) {
    emailAlreadyInUse.value = true;
    return;
  }

  try {
    emailAlreadyInUse.value = false;
    if (e?.target?.value) {
      const newEmail = e.target.value?.toLowerCase();
      if (accountStore.identityDetails?.email?.toLowerCase() != newEmail) {
        const emailIsTaken = await accountStore.isUserNameTaken(newEmail);
        emailAlreadyInUse.value = emailIsTaken;
        emit('emailIsTaken', emailIsTaken);
        return;
      }

      accountStore.fetchIdentityDetails(true);
      emailAlreadyInUse.value = false;
      emit('emailIsTaken', false);
    }
  } catch (error) {
    emit('emailIsTaken', false);
  }
};

const setFieldsFromIdentityDetails = () => {
  emailAddress.value = cloneDeep(accountStore?.identityDetails?.email ?? '');
  primaryPhoneNumber.value = cloneDeep(accountStore?.identityDetails?.phoneNumber ?? '');
  additionalPhoneNumber.value = cloneDeep(accountStore?.identityDetails?.secondaryPhoneNumber ?? '');

  nextTick(() => {
    hasChanges.value = false;
    watcherReady.value = true;
  });
};

const showUnableToSaveMessage = () => {
  toastAlertStore.addToastAlert({
    title: 'Unable to save changes',
    description: '',
    type: ToastAlertType.ERROR,
    duration: 6000,
  });
};

const onSave = async () => {
  try {
    if (accountStore.identityDetails?.email?.toLowerCase() != emailAddress.value.toLowerCase()) {
      const emailIsTaken = await accountStore.isUserNameTaken(emailAddress.value);
      if (emailIsTaken) {
        emailAlreadyInUse.value = true;
        showUnableToSaveMessage();
        return;
      }
    }

    if (
      emailEl.value?.inputRef?.node?.store?.rule_matches?.blocking ||
      primaryPhoneNumberEl.value?.node?.store?.rule_matches?.blocking ||
      additionalPhoneNumberEl.value?.node?.store?.rule_matches?.blocking
    ) {
      return;
    }

    showSaving.value = true;

    await accountStore.updateContactInformation(
      accountStore.identityDetails?.d365Id ?? '',
      emailAddress.value,
      primaryPhoneNumber.value,
      additionalPhoneNumber.value ?? null
    );

    hasChanges.value = false;
    showSaving.value = false;

    toastAlertStore.addToastAlert({
      title: 'Contact information updated',
      description: '',
      type: ToastAlertType.SUCCESS,
      duration: 6000,
    });
  } catch (error) {
    showSaving.value = false;
    showUnableToSaveMessage();
  }
};
</script>
